import * as Yup from 'yup';

import {
  email,
  name,
  nameKana,
  password,
  passwordConfirmation,
  phoneNumber,
  inputtedByParents,
  gender,
  genderExpression,
} from '../common/attributes';
import { error } from '../../utils/i18n';
import { validateDisallowedCharsWithApi } from '../../utils/api';
import API from '../../../../../lib/API';

const testDisallowedChars =
  (
    allowAscii: boolean,
    allowHankakuKatakana: boolean,
  ): Yup.TestFunction<string, Yup.AnyObject> =>
  async (value, context) => {
    const json = await validateDisallowedCharsWithApi(
      value,
      allowAscii,
      allowHankakuKatakana,
    );
    if (json.disallowed_char.contains) {
      return context.createError({
        message: error('disallowed_chars', undefined, {
          disallowed_chars: json.disallowed_char.targets,
        }),
        type: 'disallowed_chars',
      });
    }
    return true;
  };

const testUniqueEmail: Yup.TestFunction<string, Yup.AnyObject> = async (
  value,
  context,
) => {
  if (!value) {
    return true;
  } // To reduce needless API call.

  const response = await API.get(
    `/registration/accounts/email_uniqueness?email=${encodeURIComponent(
      value,
    )}`,
  );
  const json: {
    uniqueness: boolean;
    user: boolean;
    account: boolean;
  } = await response.json();

  if (!(json.account && json.user)) {
    return context.createError({
      message: error('email_uniqueness_error'),
      type: 'email_uniqueness_error',
    });
  }
  return true;
};

interface SchemaOption {
  isJuniorGrade: boolean;
}

const learnerAccountSchema = (opt: SchemaOption) => {
  return {
    learner_account: Yup.object().shape({
      last_name: name('last_name').test(testDisallowedChars(false, false)),
      first_name: name('first_name').test(testDisallowedChars(false, false)),
      last_name_kana: nameKana('last_name_kana', 30),
      first_name_kana: nameKana('first_name_kana', 30),
      phone_number: phoneNumber('phone_number'),
      email: email('email').test(testUniqueEmail),
      // TODO check availability asynchronously
      password: password('password', 'password_confirmation'),
      password_confirmation: passwordConfirmation('password_confirmation'),
      inputted_by_parents: inputtedByParents('inputted_by_parents'),
      gender: opt.isJuniorGrade ? Yup.string() : gender('gender'),
      gender_expression: opt.isJuniorGrade
        ? Yup.string()
        : genderExpression('gender_expression', (schema) =>
            schema.test(testDisallowedChars(true, true)),
          ),
    }),
  };
};

const learnerAndAccountRegistrationFormSchema = (opt: SchemaOption) => {
  return Yup.object().shape({
    ...learnerAccountSchema(opt),
  });
};

export default learnerAndAccountRegistrationFormSchema;
